import { useState, useEffect } from 'react';

export interface TokenMetadata {
  id: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  description?: string;
}

export interface TokenMetadataMap {
  [symbol: string]: TokenMetadata;
}

export const useTokenMetadata = () => {
  const [tokenMetadata, setTokenMetadata] = useState<TokenMetadataMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTokenMetadata = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch from Alephium official token list
        const response = await fetch(
          'https://raw.githubusercontent.com/alephium/token-list/refs/heads/master/tokens/mainnet.json'
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch token metadata: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Transform the array into a map keyed by symbol for fast lookup
        const metadataMap: TokenMetadataMap = {};
        
        if (Array.isArray(data.tokens)) {
          for (const token of data.tokens) {
            if (token.symbol) {
              metadataMap[token.symbol] = {
                id: token.id || token.symbol,
                symbol: token.symbol,
                name: token.name || token.symbol,
                decimals: token.decimals || 18,
                logoURI: token.logoURI,
                description: token.description
              };
            }
          }
        }

        // Add ALPH manually if not present
        if (!metadataMap['ALPH']) {
          metadataMap['ALPH'] = {
            id: '0000000000000000000000000000000000000000000000000000000000000000',
            symbol: 'ALPH',
            name: 'Alephium',
            decimals: 18,
            logoURI: 'https://raw.githubusercontent.com/alephium/alephium-brand-guide/master/logos/light/alephium_logo_monogram.svg',
            description: 'Alephium native currency'
          };
        }

        setTokenMetadata(metadataMap);
        console.log(`📋 Loaded metadata for ${Object.keys(metadataMap).length} tokens`);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Failed to fetch token metadata:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTokenMetadata();
  }, []);

  const getTokenMetadata = (symbol: string): TokenMetadata | undefined => {
    return tokenMetadata[symbol];
  };

  const getTokenLogo = (symbol: string): string | undefined => {
    return tokenMetadata[symbol]?.logoURI;
  };

  const getTokenName = (symbol: string): string => {
    return tokenMetadata[symbol]?.name || symbol;
  };

  return {
    tokenMetadata,
    loading,
    error,
    getTokenMetadata,
    getTokenLogo,
    getTokenName
  };
};
